import { css } from '@emotion/react';
import { type FC } from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { AnchorLink } from '~/_shared/baseComponents/links';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { LINK_TO_V4_ACCOUNT_SETTINGS } from '~/_shared/constants/maptive';
import { useTranslation } from '~/_shared/utils/hooks';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  s, Trans,
} from '~/translations/Trans';
import { OverlayLoaderComponent } from '../../_shared/components/overlay/overlayLoader.component';
import { MembersTableComponent } from './membersTable/membersTable.component';
import { NewMemberFormContainer } from './newMemberForm/newMemberForm.container';
import { type TeamMemberLimitsData } from './teamManagement.repository';
import { type InvitationAndMemberData } from './teamManagementModal.container';

const modalContentStyle = css({
  padding: '0',
  width: '100%',
});

const alertWrapperStyle = css({
  padding: '8px 24px',
});

type TeamManagementModalComponentProps = ModalProps<{
  isClientMigrationInProgress: boolean;
  isLoading: boolean;
  data: InvitationAndMemberData[];
  limits?: TeamMemberLimitsData;
}>;

export const TeamManagementModalComponent: FC<TeamManagementModalComponentProps> = ({
  isClientMigrationInProgress,
  isLoading,
  data,
  limits,
  ...modalProps
}) => {
  const [t] = useTranslation();

  return (
    <ModalComponent
      {...modalProps}
      additionalContent={isLoading && <OverlayLoaderComponent />}
      caption={t('Manage Users')}
      contentStyle={modalContentStyle}
      maxWidth="980px"
      skipCloseOnMapIdChange
    >
      {isClientMigrationInProgress && (
        <div css={alertWrapperStyle}>
          <TeamUserManagementNotAvailableDueToMigrationComponent />
        </div>
      )}
      {!isClientMigrationInProgress && (
        <NewMemberFormContainer
          teamMemberLimits={limits}
        />
      )}
      {!!limits && (
        <MembersTableComponent
          data={data}
          limits={limits}
        />
      )}
    </ModalComponent>
  );
};

export const TeamUserManagementNotAvailableDueToMigrationComponent: FC = _ => (
  <AlertComponent type="info" size="small">
    <Trans i18nKey="userManagement.alert.migrationInProgress">
      {s('firstPart')}
      <AnchorLink
        href={LINK_TO_V4_ACCOUNT_SETTINGS}
        target="_blank"
      >
        {s('previous')}
      </AnchorLink>
      {s('secondPart')}
    </Trans>
  </AlertComponent>
);
